// ============================================================================
// src/lib/centralClock.js — farm-local (America/Chicago) greeting clock
// ----------------------------------------------------------------------------
// Home's greeting names the time of day and the date. Both must be resolved in
// America/Chicago, not the browser's zone, so they agree with the farm day
// boundary every other Home surface already uses (missed-report checks, Last 5
// Days, daily-report uniqueness). A phone left on Eastern or Pacific time must
// still read the farm's day.
//
// Everything here is pure and takes an explicit `now`, so the boundary behavior
// is testable without touching the real clock: hand it an instant, assert the
// output. The React side owns only the scheduling.
//
// Boundaries that change what Home says:
//   12:00 Central  morning  -> afternoon
//   18:00 Central  afternoon -> evening
//   00:00 Central  evening  -> morning AND the date rolls over
// ============================================================================

export const FARM_TIME_ZONE = 'America/Chicago';

// Greeting cutoffs in farm-local hours. Ordered; each entry owns [hour, next).
const GREETINGS = Object.freeze([
  {fromHour: 0, word: 'Good morning'},
  {fromHour: 12, word: 'Good afternoon'},
  {fromHour: 18, word: 'Good evening'},
]);

// Re-check at least this often even when the next boundary is hours away, so a
// laptop that slept through noon resyncs promptly instead of showing "Good
// morning" all afternoon. Bounded: one cheap comparison, and the React side
// only re-renders when the rendered strings actually change.
export const MAX_GREETING_TICK_MS = 15 * 60 * 1000;

// Farm-local wall-clock time, already reduced to the three numbers the greeting
// and the next-boundary calculation need.
export type FarmClockParts = {
  hour: number;
  minute: number;
  second: number;
};

export type CentralGreeting = {
  word: string;
  dateText: string;
};

// Farm-local wall-clock parts for an instant. Returns null when the host has no
// usable ICU data, so callers can fall back rather than render a wrong date.
function farmParts(now: Date): FarmClockParts | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: FARM_TIME_ZONE,
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: false,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(now);
    const get = (type: Intl.DateTimeFormatPartTypes) => {
      const found = parts.find((p) => p.type === type);
      return found ? Number(found.value) : NaN;
    };
    // Intl renders midnight as hour 24 in some engines under hour12:false.
    const hour = get('hour') % 24;
    const minute = get('minute');
    const second = get('second');
    if ([hour, minute, second].some((n) => !Number.isFinite(n))) return null;
    return {hour, minute, second};
  } catch (_e) {
    return null;
  }
}

/** The greeting word for a farm-local hour. */
export function greetingWordForHour(hour: number): string {
  let word = GREETINGS[0].word;
  for (const entry of GREETINGS) {
    if (hour >= entry.fromHour) word = entry.word;
  }
  return word;
}

/**
 * The rendered greeting for an instant: the time-of-day word plus the farm's
 * real calendar date. Pure — same instant in, same strings out, regardless of
 * the host time zone.
 *
 */
export function centralGreeting(now: Date = new Date()): CentralGreeting {
  const parts = farmParts(now);
  if (!parts) {
    // No usable ICU: fall back to the host's own date rather than an empty or
    // fabricated line, and pick the word from the host hour.
    return {word: greetingWordForHour(now.getHours()), dateText: now.toDateString()};
  }
  let dateText: string;
  try {
    dateText = new Intl.DateTimeFormat('en-US', {
      timeZone: FARM_TIME_ZONE,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(now);
  } catch (_e) {
    dateText = now.toDateString();
  }
  return {word: greetingWordForHour(parts.hour), dateText};
}

/**
 * Milliseconds until the next instant at which centralGreeting() could change
 * (the next 12:00, 18:00, or farm midnight), clamped to MAX_GREETING_TICK_MS so
 * a suspended machine resyncs on wake. Always >= 1000 so a tick can never spin.
 *
 */
export function msUntilNextGreetingBoundary(now: Date = new Date()): number {
  const parts = farmParts(now);
  if (!parts) return MAX_GREETING_TICK_MS;
  const secondsNow = parts.hour * 3600 + parts.minute * 60 + parts.second;
  const boundaries = [12 * 3600, 18 * 3600, 24 * 3600];
  const nextBoundary = boundaries.find((b) => b > secondsNow) ?? 24 * 3600;
  const ms = (nextBoundary - secondsNow) * 1000;
  return Math.max(1000, Math.min(ms, MAX_GREETING_TICK_MS));
}
